import { useQuery, UseQueryResult } from 'react-query'
import { useParams } from 'react-router'
import { RouteParameters } from '../../constants'
import { Resource } from '../../types'
import { logFactory } from '../../util/logFactory'
import { useAppointmentsService } from '../services'
import { createContext, FC, useContext } from 'react'

type UseResourceContext = Omit<UseQueryResult<Resource>, 'data'> & {
  resource?: Resource
}

const hookName = 'useResource'
const log = logFactory(hookName)

const defaultContext = {
  resource: undefined,
  isError: false,
  isLoading: false,
} as unknown as UseResourceContext

const context = createContext<UseResourceContext>(defaultContext)

const { Provider } = context

const ResourceProvider: FC = ({ children }) => {
  const { [RouteParameters.RESOURCE_ID]: resourceId } = useParams()
  const { getResources, isReady } = useAppointmentsService()

  const fetchResource = async (): Promise<Resource> => {
    log(`🗓️ Fetching resource...`, resourceId)

    const {
      data: [found],
    } = await getResources({ id: resourceId, page_size: 1 })

    log(`🗓️ Found resource`, found)

    return found
  }

  const isQueryEnabled = Boolean(resourceId && isReady)

  const {
    data: resource,
    isLoading,
    ...results
  } = useQuery([hookName, resourceId], fetchResource, {
    enabled: isQueryEnabled,
  })

  const contextValue: UseResourceContext = {
    ...results,
    isLoading: Boolean(!isReady || isLoading),
    resource,
  }

  return <Provider value={contextValue}>{children}</Provider>
}

const useResource = (): UseResourceContext => useContext(context)

export { ResourceProvider, useResource }
